import appConfig from '../config/appConfig';
import * as reconnectGuideTypes from '../consts/app/reconnectGuideTypes';
import * as localizationKeys from '../consts/localization/localizationKeys';
import ModalTypes from '../enums/app/modalTypes';
import {removeGamAuthData, setFetchTokenInProgress} from '../state/ducks/auth';
import {hideAllLoaders, setIsStartAutoSync, setNotificationFailed, setScpCheckInProgress} from '../state/ducks/global';
import {
    clearIotDevice,
    clearIotDeviceHolderData,
    setFWUCheking,
    setIsDisconnected,
    setIsPairingInProgress,
} from '../state/ducks/iotDevice/actions';
import {hideModalAction, showReconnectDeviceGuideModalAction} from '../state/ducks/modal';
import {clearMwDevice, setMwDeviceIsDisconnectedWithNoReconnect} from '../state/ducks/mwDevice';
import {
    clearYapAssets,
    setDeviceInfoReceived,
    setHolderInfoReceived,
    setOfflineYapFirmwareInProgress,
    setSyncLastRequestId,
    setYapFirmwareInProgress,
    setYapHolderConnected,
    setYapLastRequestId,
    setYapSyncInProgress,
    setYapUpdateSettingsInProgress,
} from '../state/ducks/yapEncrypted';
import {makeSelectIsDeviceConnected} from '../state/selectors/iotDevice';
import {makeSelectIsYapFirmwareInProgress} from '../state/selectors/yapEncrypted';
import {dispatch, getState} from '../state/store';
import appErrorService from './app/appErrorService';
import backendService from './app/backendService';
import authDataStoreService from './auth/authDataStoreService';
import deviceCharacteristicService from './ble/deviceCharacteristicService';
import cmClientService from './communicationLayer/cmClientService';
import connectivityService from './device/connectivityService';
import deviceIdlePingScheduler from './device/deviceIdlePingScheduler';
import devicePreSyncPingScheduler from './device/devicePreSyncPingScheduler';
import framesProcessingService from './device/framesProcessingService';
import IqosServiceClient from './device/iqosServiceClient';
import deviceConnectionDetectorService from './deviceConnectionDetectorService';
import DisconnectEventEmitter from './events/disconnectEventEmitter';
import hidReconnectHandlerService from './hid/hidReconnectHandlerService';
import log from './logger/log';
import appRouterService from './route/appRouterService';
import * as dataCollectionService from './uam/dataCollectionService';
import userDeviceService from './user/userDeviceService';
import yapFlowTimeoutService from './yap/yapFlowTimeoutService';
import yapWebSocketService from './yap/yapWebSocketService';

const onAvailabilityChanged = (e) => {
    const isEnabled = e.target.value;

    log.info(`iqosBleService: onAvailabilityChanged, isEnabled: ${isEnabled}`);

    if (!isEnabled) {
        appRouterService.forwardToConnectionFlowPage();
        //IA: exactly order that is needed
        //    first forward
        //    than clearDevice
        disconnectAndClearState();
        dispatch(hideAllLoaders());
        dispatch(setNotificationFailed({textKey: localizationKeys.BLE_GLOBAL_ERROR_NOTIFICATION_TEXT}));
    }
};

const addBleAvailabilityListener = () => {
    if (userDeviceService.isBluetoothEventListenersSupported()) {
        navigator.bluetooth.removeEventListener('availabilitychanged', onAvailabilityChanged);
        navigator.bluetooth.addEventListener('availabilitychanged', onAvailabilityChanged);
    }
};

const disconnectIqosClient = () => {
    const iqosService = new IqosServiceClient();

    if (iqosService.isClientInitialized()) {
        iqosService.disconnect();
    }
};

const disconnectAndClearState = () => {
    disconnectIqosClient();
    const iqosService = new IqosServiceClient();

    if (iqosService.isClientInitialized()) {
        iqosService.disconnect();
    }
    hidReconnectHandlerService.clearHidReconnectHandlerState();

    const isGamBackend = backendService.isGamBackend();
    const isUamBackend = backendService.isUamBackend();

    if (isUamBackend) {
        disconnectAndClearUamState();
    }

    if (isGamBackend) {
        dispatch(removeGamAuthData());
    }

    disconnectAndClearYapState();

    dispatch(setFetchTokenInProgress(false));
    dispatch(setIsPairingInProgress(false));
    dispatch(clearMwDevice());
    dispatch(clearIotDevice());
};

const disconnectAndClearYapState = () => {
    yapFlowTimeoutService.clearAllFlowsTimeout();
    cmClientService.disconnectCmClient();
    yapWebSocketService.disconnectYapWebSocket();
    dispatch(setYapLastRequestId());
    dispatch(setSyncLastRequestId());
    dispatch(setYapSyncInProgress(false));
    dispatch(setYapUpdateSettingsInProgress(false));
    dispatch(setYapFirmwareInProgress(false));
    dispatch(setIsStartAutoSync(false));
    dispatch(setOfflineYapFirmwareInProgress(false));
    dispatch(setDeviceInfoReceived(false));
    dispatch(setScpCheckInProgress(false));
    dispatch(clearYapAssets());
    disconnectAndClearYapHolderState();
};

const disconnectAndClearUamState = () => {
    authDataStoreService.clearUamApiToken();
    dataCollectionService.setDataCollectionFlowInProgress(false);
    dataCollectionService.setDataCollectionFlowReceived();
    disconnectAndClearUamHolderState();
};

//*********** Holder State ********** //
const disconnectAndClearHolderState = () => {
    disconnectAndClearYapHolderState();
    disconnectAndClearUamHolderState();
    dispatch(clearYapAssets(true));
    dispatch(clearIotDeviceHolderData());
};

const disconnectAndClearYapHolderState = () => {
    yapWebSocketService.disconnectYapWebSocket(true);
    cmClientService.disconnectCmClient(true);
    dispatch(setYapLastRequestId(undefined, true));
    dispatch(setSyncLastRequestId(undefined, true));
    dispatch(setYapSyncInProgress(false, true));
    dispatch(setYapUpdateSettingsInProgress(false, true));
    dispatch(setHolderInfoReceived(false));
    dispatch(setYapHolderConnected(false));
};

const disconnectAndClearUamHolderState = () => {
    authDataStoreService.clearUamApiToken(true);
    dataCollectionService.setDataCollectionFlowReceived({isHolder: true});
};
//*********** End Holder State ********** //

const disconnectAndForwardToStartPage = (isDeviceDisconnected, modalTypeToHide = ModalTypes.HID_RECONNECT) => {
    if (!isDeviceDisconnected) {
        disconnectAndClearState();
    }

    dispatch(hideModalAction(modalTypeToHide));
    appRouterService.forwardToConnectionFlowPage();
};

const disconnectAndShowCriticalGuide = () => {
    disconnectAndForwardToStartPage();
    dispatch(showReconnectDeviceGuideModalAction({guideType: reconnectGuideTypes.CRITICAL}));
};

const disconnectAndShowReconnectError = () => {
    const isReconnectDeviceGuideEnabled = appConfig.getIsReconnectDeviceGuideEnabled();

    if (isReconnectDeviceGuideEnabled) {
        disconnectAndShowCriticalGuide();
    } else {
        appErrorService.showIoErrorWithAppReset();
    }
};

const onPairingError = () => disconnectAndClearState();

const stopDeviceSchedulers = () => {
    framesProcessingService.disable();
    devicePreSyncPingScheduler.stopPing();
    deviceIdlePingScheduler.killScheduler();
};

const onDeviceDisconnect = (isDisconnectedByUser) => {
    const state = getState();
    const isDeviceConnected = makeSelectIsDeviceConnected()(state);

    // do nothing if status of the device is not connected
    if (!isDeviceConnected) return;

    stopDeviceSchedulers();

    const isUsb = connectivityService.isUsb();

    if (!isUsb) {
        const disconnectEventEmitter = new DisconnectEventEmitter();

        disconnectEventEmitter.emitDeviceDisconnect();
        disconnectEventEmitter.removeAllDisconnectListeners();

        deviceCharacteristicService.removeCharacteristicsListeners();
    }

    if (isDisconnectedByUser) {
        dispatch(setMwDeviceIsDisconnectedWithNoReconnect(true));
    } else {
        if (isUsb) {
            hidReconnectHandlerService.showHidReconnectModal();
        } else {
            deviceConnectionDetectorService.showFailedReconnectDeviceGuide();
        }
    }

    dispatch(setIsDisconnected());

    const isYapFirmwareInProgress = makeSelectIsYapFirmwareInProgress()(state);

    if (isYapFirmwareInProgress) dispatch(setFWUCheking());

    dataCollectionService.setDataCollectionFlowReceived();
    dataCollectionService.setDataCollectionFlowReceived({isHolder: true});
    dispatch(setDeviceInfoReceived(false));
    dispatch(setHolderInfoReceived(false));
};

const isDeviceConnected = () => {
    const iqosService = new IqosServiceClient();

    return iqosService.isDeviceConnected();
};

export default {
    addBleAvailabilityListener,
    disconnectAndClearState,
    disconnectAndClearHolderState,
    disconnectAndForwardToStartPage,
    disconnectAndShowCriticalGuide,
    isDeviceConnected,
    onDeviceDisconnect,
    onPairingError,
    stopDeviceSchedulers,
    disconnectAndShowReconnectError,
};
